import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Check,
  FileText,
  Beaker,
  Upload,
  Rocket,
  BarChart3,
} from 'lucide-react';
import TutorialScriptBuilderMockup from './TutorialScriptBuilderMockup.js';
import TutorialTestChannelsMockup from './TutorialTestChannelsMockup.js';
import TutorialCrmUploadMockup from './TutorialCrmUploadMockup.js';
import TutorialUseChannelsMockup from './TutorialUseChannelsMockup.js';
import TutorialAnalyticsMockup from './TutorialAnalyticsMockup.js';

const STEP_ICONS = [FileText, Beaker, Upload, Rocket, BarChart3];

const STEPS = [
  {
    title: 'Create Script',
    body:
      'Open Train → Agents. Build what your AI says and how it behaves: write the prompt and script in the main editor, then use the right rail to open Agent settings (voice, model, names) and save.',
    showScriptMockup: true,
    actions: [{ label: 'Open Agents', menu: 'Agents' }],
  },
  {
    title: 'Test',
    body:
      'Validate before customers see it. Deploy → Chatbot for text, or Outbound / Inbound for voice using numbers that already have your script assigned. Iterate in Agents if something is off.',
    showTestMockup: true,
    actions: [
      { label: 'Open Chatbot', menu: 'Chatbot' },
      { label: 'Open Outbound', menu: 'Outbound' },
    ],
  },
  {
    title: 'Upload & CRM',
    body:
      'Knowledge Base (Train) holds uploads the model can cite. CRM (Insights) tracks leads and outcomes. Tie collections to lines under Phone Numbers where your deployment supports it.',
    showCrmMockup: true,
    actions: [
      { label: 'Knowledge Base', menu: 'Knowledge Base' },
      { label: 'CRM', menu: 'CRM' },
    ],
  },
  {
    title: 'Use',
    body:
      'Account → Phone Numbers assigns scripts and options per line. Batch Call dials your list with an agent; Outbound and Inbound handle live lines; Chatbot can be embedded on your site as a widget for visitors.',
    showUseMockup: true,
    actions: [
      { label: 'Phone Numbers', menu: 'Phone Numbers' },
      { label: 'Batch Call', menu: 'Batch Call' },
      { label: 'Chatbot', menu: 'Chatbot' },
      { label: 'Outbound', menu: 'Outbound' },
    ],
  },
  {
    title: 'Analysis',
    body:
      'Logs hold transcripts; General Analytics summarizes survey-style metrics; Analytics Setup defines what is measured. Use insights to refine scripts and routing.',
    showAnalyticsMockup: true,
    actions: [
      { label: 'Logs', menu: 'Logs' },
      { label: 'Analytics Setup', menu: 'Analytics Setup' },
      { label: 'General Analytics', menu: 'General Analytics' },
    ],
  },
];

/**
 * Multi-step dashboard tutorial (modal).
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {(menuItem: string) => void} onNavigate - selects a sidebar page by menu name
 */
export default function DashboardTutorialModal({ open, onClose, onNavigate }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setIndex(0);
    }
  }, [open]);

  const step = STEPS[index];
  const total = STEPS.length;
  const canPrev = index > 0;
  const canNext = index < total - 1;

  const handleKeyDown = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && canPrev) setIndex((i) => i - 1);
      if (e.key === 'ArrowRight' && canNext) setIndex((i) => i + 1);
    },
    [open, onClose, canPrev, canNext]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!open) return null;

  const StepIcon = STEP_ICONS[index] || HelpCircle;

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dashboard-tutorial-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-[min(90rem,calc(100vw-1rem))] rounded-2xl bg-white shadow-2xl border border-gray-200/80 overflow-hidden flex flex-col max-h-[min(98vh,1600px)] min-h-[min(75vh,560px)] md:min-h-[min(92vh,1020px)]">
        <div className="shrink-0 px-6 sm:px-8 py-5 border-b border-gray-100 flex items-start gap-4 bg-gradient-to-r from-[#459EA3] to-[#3d8a8e] text-white">
          <div className="rounded-2xl bg-white/20 p-3 shadow-inner">
            <StepIcon className="w-8 h-8 sm:w-9 sm:h-9" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-white/85">Getting started</p>
            <h2 id="dashboard-tutorial-title" className="text-xl sm:text-2xl font-bold leading-tight mt-0.5">
              {step.title}
            </h2>
            <p className="text-sm text-white/85 mt-1">
              Step {index + 1} of {total} — use the step bar, arrows, or Next / Back
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2.5 text-white/90 hover:bg-white/20 transition-colors shrink-0"
            aria-label="Close tutorial"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="shrink-0 px-3 sm:px-6 py-4 sm:py-5 bg-[#f8fafb] border-b border-gray-100">
          <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3 sm:mb-4 text-center sm:text-left">
            Tutorial steps
          </p>
          <div className="overflow-x-auto pb-1 -mx-1 px-1">
            <div className="flex w-full min-w-[min(100%,36rem)] sm:min-w-0 items-start justify-between gap-0">
              {STEPS.map((s, i) => {
                const done = i < index;
                const active = i === index;
                return (
                  <React.Fragment key={s.title}>
                    {i > 0 && (
                      <div
                        className="flex-1 flex items-center min-w-[12px] pt-[18px] px-0.5 sm:px-1"
                        aria-hidden
                      >
                        <div
                          className={`h-[3px] w-full rounded-full transition-colors ${
                            i <= index ? 'bg-[#459EA3]' : 'bg-gray-200'
                          }`}
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setIndex(i)}
                      className="flex flex-col items-center gap-2 shrink-0 w-[4.25rem] sm:w-[5.5rem] md:w-auto md:min-w-[6.5rem] max-w-[7rem] md:max-w-[9rem] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#459EA3] focus-visible:ring-offset-2 rounded-lg py-0.5"
                      aria-current={active ? 'step' : undefined}
                      aria-label={`Step ${i + 1}: ${s.title}`}
                    >
                      <span
                        className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold border-2 transition-all ${
                          done
                            ? 'border-[#459EA3] bg-[#459EA3] text-white shadow-sm'
                            : active
                              ? 'border-[#459EA3] bg-[#DBF4F3] text-[#459EA3] ring-4 ring-[#459EA3]/20 shadow-sm'
                              : 'border-gray-300 bg-white text-gray-400'
                        }`}
                      >
                        {done ? <Check className="w-4 h-4 stroke-[3]" aria-hidden /> : i + 1}
                      </span>
                      <span
                        className={`text-[9px] sm:text-[10px] md:text-xs font-semibold text-center leading-tight line-clamp-2 md:line-clamp-none ${
                          active ? 'text-[#459EA3]' : done ? 'text-gray-800' : 'text-gray-400'
                        }`}
                      >
                        {s.title}
                      </span>
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 sm:px-8 py-6">
          <p className="text-base text-gray-800 leading-relaxed">{step.body}</p>

          {step.showScriptMockup && <TutorialScriptBuilderMockup />}

          {step.showTestMockup && <TutorialTestChannelsMockup />}

          {step.showCrmMockup && <TutorialCrmUploadMockup />}

          {step.showUseMockup && <TutorialUseChannelsMockup />}

          {step.showAnalyticsMockup && <TutorialAnalyticsMockup />}

          {step.actions?.length > 0 && (
            <div
              className={`${
                step.showScriptMockup ||
                step.showTestMockup ||
                step.showCrmMockup ||
                step.showUseMockup ||
                step.showAnalyticsMockup
                  ? 'mt-8'
                  : 'mt-6'
              } pt-6 border-t border-gray-200`}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Open in dashboard</p>
              <div className="flex flex-wrap gap-3">
                {step.actions.map((a) => (
                  <button
                    key={a.menu}
                    type="button"
                    onClick={() => {
                      onNavigate?.(a.menu);
                      onClose();
                    }}
                    className="inline-flex items-center rounded-xl border-2 border-[#459EA3]/35 bg-[#DBF4F3]/50 px-4 py-2.5 text-sm font-semibold text-[#2d7a7e] hover:bg-[#DBF4F3] hover:border-[#459EA3]/60 transition-colors shadow-sm"
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="shrink-0 px-6 sm:px-8 py-5 border-t border-gray-100 bg-gray-50/90 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={!canPrev}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed order-2 sm:order-1"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>

          <div className="flex justify-center gap-2 order-1 sm:order-2" aria-hidden>
            {STEPS.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                className={`h-2.5 rounded-full transition-all ${
                  i === index ? 'w-8 bg-[#459EA3]' : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to step ${i + 1}`}
              />
            ))}
          </div>

          {canNext ? (
            <button
              type="button"
              onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#459EA3] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#3d8a8e] shadow-md order-3"
            >
              Next
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-xl bg-[#459EA3] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#3d8a8e] shadow-md order-3"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
